import { useState, useEffect, useCallback } from 'react';
import { FileText, CheckCircle, HelpCircle, X, User, Mail, Calendar, Clock, Paperclip, Download, Shield, AlertCircle, Loader2 } from 'lucide-react';
import Swal from 'sweetalert2';
import ChatModal from './ChatModal';
import UserRequestModal from './UserRequestModal';
import { trustCenterApi, documentsApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const TYPE_MAP = {
    document:   { label: 'Document',   icon: 'document',   border: 'border-l-4 border-l-blue-500' },
    documents:  { label: 'Document',   icon: 'document',   border: 'border-l-4 border-l-blue-500' },
    assessment: { label: 'Assessment', icon: 'assessment', border: 'border-l-4 border-l-indigo-500' },
    query:      { label: 'Query',      icon: 'query',      border: 'border-l-4 border-l-accent-500' },
};

const normalizeRequest = (item) => {
    const raw = (item.requestType || item.type || 'query').toLowerCase();
    const meta = TYPE_MAP[raw] || TYPE_MAP.query;
    return {
        id:           item.id,
        title:        item.title || item.queryAbout || item.message || 'Untitled',
        date:         item.createdAt
                        ? new Date(item.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                        : item.date || '',
        type:         meta.label,
        status:       item.status || 'Pending',
        icon:         meta.icon,
        borderColor:  meta.border,
        requester:    item.fullName || item.requester || '',
        email:        item.email || '',
        organization: item.organization || '',
        message:      item.message || '',
        attachments:  item.attachments ?? [],
    };
};

// Normalize a raw document record from the API
const normalizeDocument = (doc) => ({
    id:               doc.id,
    documentName:     doc.documentName || doc.name || 'Untitled Document',
    documentType:     doc.documentType || '—',
    organizationName: doc.organizationName || '—',
    startDate:        doc.startDate ? new Date(doc.startDate) : null,
    endDate:          doc.endDate   ? new Date(doc.endDate)   : null,
    uploadedByName:   doc.uploadedByName || doc.uploadedBy || null,
    fileName:         doc.fileName || doc.originalFileName || doc.documentName || 'document',
    fileUrl:          doc.fileUrl || doc.downloadUrl || null,
});

const UserRequests = () => {
    const { user } = useAuth();

    // Page-level view: 'requests' | 'documents'
    const [view, setView] = useState('requests');

    // Requests state
    const [activeTab, setActiveTab] = useState('All');
    const [selected, setSelected] = useState(null);
    const [chatRequest, setChatRequest] = useState(null);
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showRequestModal, setShowRequestModal] = useState(false);

    // My Documents state
    const [myDocuments, setMyDocuments] = useState([]);
    const [docsLoading, setDocsLoading] = useState(false);
    const [docsError, setDocsError] = useState(null);
    const [downloadingId, setDownloadingId] = useState(null);

    useEffect(() => {
        setLoading(true);
        trustCenterApi
            .getRequests(1, 100)
            .then((res) => {
                const items = Array.isArray(res) ? res : res?.data ?? [];
                setRequests(items.map(normalizeRequest));
            })
            .catch((err) => setError(err.message || 'Failed to load requests.'))
            .finally(() => setLoading(false));
    }, []);

    const fetchMyDocuments = useCallback(() => {
        if (!user?.id && !user?.email) return;
        setDocsLoading(true);
        setDocsError(null);
        documentsApi
            .getForUser(user.id || null, user.email || null)
            .then((res) => {
                const items = Array.isArray(res) ? res : res?.data ?? [];
                // Client-side guard: only show docs explicitly shared with this user
                const filtered = items.filter((doc) => {
                    const byId = user?.id && doc.sharedWithUserId &&
                        String(doc.sharedWithUserId) === String(user.id);
                    const byEmail = user?.email && doc.sharedWithUserEmail &&
                        doc.sharedWithUserEmail.toLowerCase() === user.email.toLowerCase();
                    return byId || byEmail;
                });
                setMyDocuments(filtered.map(normalizeDocument));
            })
            .catch((err) => setDocsError(err.message || 'Failed to load your documents.'))
            .finally(() => setDocsLoading(false));
    }, [user?.id, user?.email]);

    // Fetch once on mount so we know whether to show the My Documents tab
    useEffect(() => {
        fetchMyDocuments();
    }, [fetchMyDocuments]);

    const handleDownload = async (doc) => {
        // Check if document is expired before attempting download
        const isExpired = isDocExpired(doc);
        
        if (isExpired) {
            Swal.fire({
                icon: 'error',
                title: 'Document Access Expired',
                html: `<p>Your access to <strong>${doc.documentName}</strong> has expired.</p><p class="mt-2" style="font-size:13px;color:#666">Please contact your administrator for more information.</p>`,
                confirmButtonColor: '#F47F2E',
                confirmButtonText: 'OK',
            });
            return;
        }

        setDownloadingId(doc.id);
        setDocsError(null);
        try {
            const result = await documentsApi.download(doc.id);
            if (result.redirectUrl) {
                // Backend returned a signed/SAS URL — trigger browser download via anchor
                const a = document.createElement('a');
                a.href = result.redirectUrl;
                a.download = doc.fileName || 'document';
                a.rel = 'noopener noreferrer';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            } else {
                // Backend streamed the file binary
                const objectUrl = URL.createObjectURL(result.blob);
                const a = document.createElement('a');
                a.href = objectUrl;
                a.download = result.filename || doc.fileName || 'document';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(objectUrl);
            }
        } catch (err) {
            setDocsError(`Download failed for "${doc.documentName}". Please contact your administrator.`);
        } finally {
            setDownloadingId(null);
        }
    };

    const tabs = ['All', 'Documents', 'Assessments', 'Queries'];

    const getStatusColor = (status) => {
        switch (status) {
            case 'Accepted':
                return 'bg-green-100 text-green-800 border border-green-300';
            case 'Rejected':
                return 'bg-red-100 text-red-800 border border-red-300';
            case 'Pending':
                return 'bg-yellow-100 text-yellow-800 border border-yellow-300';
            default:
                return 'bg-gray-100 text-gray-800 border border-gray-300';
        }
    };

    const getTypeColor = (type) => {
        switch (type) {
            case 'Document':
                return 'bg-blue-50 text-blue-700 border border-blue-200';
            case 'Assessment':
                return 'bg-indigo-50 text-indigo-700 border border-indigo-200';
            case 'Query':
                return 'bg-accent-50 text-accent-700 border border-accent-200';
            default:
                return 'bg-gray-50 text-gray-700 border border-gray-200';
        }
    };

    const getIconComponent = (type) => {
        switch (type) {
            case 'document':
                return <FileText className="w-8 h-8 text-blue-500" />;
            case 'assessment':
                return <CheckCircle className="w-8 h-8 text-indigo-500" />;
            case 'query':
                return <HelpCircle className="w-8 h-8 text-accent-500" />;
            default:
                return null;
        }
    };

    const closeModal = () => setSelected(null);

    const isDocExpired = (doc) => doc.endDate && doc.endDate < new Date();

    return (
        <div className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-slate-900 dark:to-slate-800">
            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-slate-800 dark:text-white">
                        {view === 'requests' ? 'My Requests' : 'My Documents'}
                    </h2>
                    <div className="flex items-center gap-3">
                        {/* View Toggle — only shown when this user has documents shared with them */}
                        {myDocuments.length > 0 && (
                            <div className="flex rounded-lg border border-slate-200 bg-white overflow-hidden">
                                <button
                                    onClick={() => setView('requests')}
                                    className={`px-4 py-2 text-sm font-semibold transition-colors ${
                                        view === 'requests' ? 'bg-blue-500 text-white' : 'text-slate-600 hover:bg-slate-50'
                                    }`}
                                >
                                    My Requests
                                </button>
                                <button
                                    onClick={() => setView('documents')}
                                    className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold transition-colors ${
                                        view === 'documents' ? 'bg-blue-500 text-white' : 'text-slate-600 hover:bg-slate-50'
                                    }`}
                                >
                                    My Documents
                                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#F47F2E] text-white text-xs font-bold">
                                        {myDocuments.length}
                                    </span>
                                </button>
                            </div>
                        )}
                        {view === 'requests' && (
                            <button
                                onClick={() => setShowRequestModal(true)}
                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-[#F47F2E] hover:bg-[#d96d23] rounded-lg transition-colors"
                            >
                                Send a request
                            </button>
                        )}
                    </div>
                </div>

                {/* Tab Navigation (requests view only) */}
                {view === 'requests' && (
                    <div className="flex flex-wrap gap-3">
                        {tabs.map((tab) => {
                            const count = requests.filter(
                                (req) =>
                                    tab === 'All' ||
                                    (tab === 'Documents' && req.type === 'Document') ||
                                    (tab === 'Assessments' && req.type === 'Assessment') ||
                                    (tab === 'Queries' && req.type === 'Query')
                            ).length;

                            return (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`px-6 py-2 rounded-full font-semibold transition-all ${activeTab === tab
                                            ? 'bg-blue-500 text-white shadow-lg'
                                            : 'bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-600 hover:border-blue-300'
                                        }`}
                                >
                                    {tab} <span className="ml-2 font-bold">{count}</span>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* ── My Documents View ── */}
            {view === 'documents' && (
                <div>
                    {docsLoading && (
                        <div className="flex items-center justify-center py-16 text-slate-500 gap-3">
                            <Loader2 className="w-5 h-5 animate-spin" />
                            <span className="text-sm">Loading your documents…</span>
                        </div>
                    )}
                    {docsError && (
                        <div className="flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm mb-4">
                            <AlertCircle size={16} className="flex-shrink-0" />
                            <span>{docsError}</span>
                            <button
                                onClick={fetchMyDocuments}
                                className="ml-auto text-red-600 underline hover:no-underline text-xs"
                            >
                                Retry
                            </button>
                        </div>
                    )}
                    {!docsLoading && !docsError && myDocuments.length === 0 && (
                        <div className="flex flex-col items-center justify-center py-16 text-slate-400 gap-3">
                            <Shield className="w-12 h-12 opacity-30" />
                            <p className="text-sm font-medium">No documents have been shared with you yet.</p>
                            <p className="text-xs">Documents uploaded and shared by your organization will appear here.</p>
                        </div>
                    )}
                    {!docsLoading && !docsError && myDocuments.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {myDocuments.map((doc) => {
                                const expired = isDocExpired(doc);
                                const downloading = downloadingId === doc.id;
                                return (
                                    <div
                                        key={doc.id}
                                        className={`bg-white rounded-xl shadow-sm border p-5 flex flex-col gap-3 transition-shadow hover:shadow-md ${
                                            expired ? 'border-red-200' : 'border-slate-200'
                                        }`}
                                    >
                                        {/* Document icon + name */}
                                        <div className="flex items-start gap-3">
                                            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                                                <FileText className="w-5 h-5 text-[#0077b5]" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-semibold text-slate-800 truncate" title={doc.documentName}>
                                                    {doc.documentName}
                                                </p>
                                                <p className="text-xs text-slate-400 mt-0.5">{doc.organizationName}</p>
                                            </div>
                                            {expired && (
                                                <span className="flex-shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-600 border border-red-200">
                                                    Expired
                                                </span>
                                            )}
                                        </div>

                                        {/* Type badge */}
                                        <div>
                                            <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                                                {doc.documentType}
                                            </span>
                                        </div>

                                        {/* Validity dates */}
                                        <div className="grid grid-cols-2 gap-2">
                                            <div>
                                                <p className="font-semibold text-slate-400 uppercase tracking-wide mb-0.5" style={{ fontSize: '10px' }}>Valid From</p>
                                                <p className="text-xs text-slate-600">
                                                    {doc.startDate
                                                        ? doc.startDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                                                        : '—'}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="font-semibold text-slate-400 uppercase tracking-wide mb-0.5" style={{ fontSize: '10px' }}>Valid Until</p>
                                                <p className={`text-xs ${expired ? 'text-red-500 font-medium' : 'text-slate-600'}`}>
                                                    {doc.endDate
                                                        ? doc.endDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                                                        : '—'}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Shared by */}
                                        {doc.uploadedByName && (
                                            <p className="text-xs text-slate-400">
                                                Shared by <span className="font-medium text-slate-600">{doc.uploadedByName}</span>
                                            </p>
                                        )}

                                        {/* Download */}
                                        <div className="mt-auto pt-2 border-t border-slate-100">
                                            <button
                                                onClick={() => handleDownload(doc)}
                                                disabled={downloading}
                                                className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-[#0077b5] text-white text-xs font-semibold hover:bg-[#005f8e] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                            >
                                                {downloading ? (
                                                    <><Loader2 size={13} className="animate-spin" /> Downloading…</>
                                                ) : (
                                                    <><Download size={13} /> Download</>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* ── Requests View ── */}
            {view === 'requests' && (
            <div className="space-y-4">
                {loading && (
                    <div className="bg-white dark:bg-slate-700 rounded-lg p-8 text-center text-slate-500 dark:text-slate-400">
                        Loading requests…
                    </div>
                )}
                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm">
                        {error}
                    </div>
                )}
                {!loading && !error && requests.filter((request) => {
                    if (activeTab === 'All') return true;
                    if (activeTab === 'Documents' && request.type === 'Document') return true;
                    if (activeTab === 'Assessments' && request.type === 'Assessment') return true;
                    if (activeTab === 'Queries' && request.type === 'Query') return true;
                    return false;
                }).map((request) => (
                    <div
                        key={request.id}
                        onClick={() => setSelected(request)}
                        className={`${request.borderColor} bg-white dark:bg-slate-700 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow duration-200 cursor-pointer`}
                    >
                        <div className="flex items-start justify-between">
                            {/* Left Content */}
                            <div className="flex items-start gap-4 flex-1">
                                <div className="mt-1">
                                    {getIconComponent(request.icon)}
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">
                                        {request.title}
                                    </h3>
                                    <p className="text-sm text-slate-600 dark:text-slate-400">
                                        📅 {request.date}
                                    </p>
                                </div>
                            </div>

                            {/* Right Content - Badges */}
                            <div className="flex items-center gap-3 flex-wrap justify-end">
                                <span className={`px-4 py-1 rounded-full text-sm font-medium ${getTypeColor(request.type)}`}>
                                    {request.type}
                                </span>
                                <span className={`px-4 py-1 rounded-full text-sm font-medium ${getStatusColor(request.status)}`}>
                                    {request.status}
                                </span>
                            </div>
                        </div>
                    </div>
                ))}
                {!loading && !error && requests.filter((req) => {
                    if (activeTab === 'All') return true;
                    if (activeTab === 'Documents' && req.type === 'Document') return true;
                    if (activeTab === 'Assessments' && req.type === 'Assessment') return true;
                    if (activeTab === 'Queries' && req.type === 'Query') return true;
                    return false;
                }).length === 0 && (
                    <div className="bg-white dark:bg-slate-700 rounded-lg p-8 text-center text-slate-400 text-sm">
                        No requests found.
                    </div>
                )}
            </div>
            )}

            {/* ── Detail Modal ── */}
            {selected && (() => {
                const isQuery = selected.type === 'Query';
                const headerColor = selected.type === 'Document'
                    ? 'linear-gradient(135deg, #0077b5 0%, #005f8e 100%)'
                    : selected.type === 'Assessment'
                        ? 'linear-gradient(135deg, #4f46e5 0%, #3730a3 100%)'
                        : 'linear-gradient(135deg, #F47F2E 0%, #d96d23 100%)';

                return (
                    <div className="fixed inset-0 z-50">
                        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={closeModal} />
                        <div className="relative flex min-h-full items-center justify-center p-4">
                            <div
                                className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[calc(100vh-2rem)]"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {/* Header */}
                                <div className="relative px-6 py-5 text-white" style={{ background: headerColor }}>
                                    <button
                                        onClick={closeModal}
                                        className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-md border border-white/25 text-white/85 hover:bg-white/10 transition-colors"
                                    >
                                        <X size={16} />
                                    </button>
                                    <div className="flex items-center gap-3">
                                        <div className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,255,255,0.18)' }}>
                                            {getIconComponent(selected.icon)}
                                        </div>
                                        <div>
                                            <span className="inline-block text-xs font-semibold tracking-wider uppercase px-2 py-0.5 rounded-full mb-1" style={{ background: 'rgba(255,255,255,0.2)', color: 'white' }}>
                                                {selected.type}
                                            </span>
                                            <h2 className="text-xl font-bold leading-tight">{selected.title}</h2>
                                        </div>
                                    </div>
                                </div>

                                {/* Body */}
                                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3">
                                            <div className="flex items-center gap-2 text-[11px] font-semibold tracking-wider uppercase text-slate-400 mb-1">
                                                <User size={12} /> Requester
                                            </div>
                                            <p className="text-sm font-semibold text-slate-700">{selected.requester}</p>
                                            <p className="text-xs text-slate-400">{selected.organization}</p>
                                        </div>
                                        <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3">
                                            <div className="flex items-center gap-2 text-[11px] font-semibold tracking-wider uppercase text-slate-400 mb-1">
                                                <Mail size={12} /> Email
                                            </div>
                                            <p className="text-sm font-semibold text-slate-700 truncate">{selected.email}</p>
                                        </div>
                                        <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3">
                                            <div className="flex items-center gap-2 text-[11px] font-semibold tracking-wider uppercase text-slate-400 mb-1">
                                                <Calendar size={12} /> Submitted
                                            </div>
                                            <p className="text-sm font-semibold text-slate-700">{selected.date}</p>
                                        </div>
                                        <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3">
                                            <div className="flex items-center gap-2 text-[11px] font-semibold tracking-wider uppercase text-slate-400 mb-1">
                                                <Clock size={12} /> Status
                                            </div>
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${getStatusColor(selected.status)}`}>
                                                {selected.status}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Message */}
                                    {selected.message && (
                                        <div>
                                            <p className="text-[11px] font-semibold tracking-wider uppercase text-slate-400 mb-2">Message</p>
                                            <div className="bg-white border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-600 leading-relaxed">
                                                {selected.message}
                                            </div>
                                        </div>
                                    )}

                                    {/* Attachments */}
                                    {selected.attachments && selected.attachments.length > 0 && (
                                        <div>
                                            <p className="text-[11px] font-semibold tracking-wider uppercase text-slate-400 mb-2">Attachments</p>
                                            <ul className="space-y-2">
                                                {selected.attachments.map((file, idx) => (
                                                    <li key={idx} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5">
                                                        <div className="flex items-center gap-3 min-w-0">
                                                            <Paperclip size={14} className="text-blue-500 flex-shrink-0" />
                                                            <div className="min-w-0">
                                                                <p className="text-sm font-medium text-slate-700 truncate">{file.name}</p>
                                                                <p className="text-xs text-slate-400">{file.size}</p>
                                                            </div>
                                                        </div>
                                                        <button className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-blue-500 text-blue-500 text-xs font-semibold hover:bg-blue-50 transition-colors">
                                                            <Download size={12} /> Download
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>

                                {/* Footer */}
                                <div className="flex items-center justify-between gap-3 px-6 py-3 border-t border-slate-200 bg-white">
                                    <button onClick={closeModal} className="px-4 py-2 text-sm font-medium text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50">
                                        Close
                                    </button>
                                    {/* {isQuery && (
                                        <button
                                            onClick={() => { closeModal(); setChatRequest(selected); }}
                                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-[#F47F2E] text-[#F47F2E] text-sm font-semibold hover:bg-orange-50 transition-colors"
                                        >
                                            Reply
                                        </button>
                                    )} */}
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Chat Modal for Query replies */}
            <ChatModal
                isOpen={!!chatRequest}
                onClose={() => setChatRequest(null)}
                request={chatRequest}
            />

            {/* Send a Request Modal */}
            <UserRequestModal
                isOpen={showRequestModal}
                onClose={() => setShowRequestModal(false)}
            />
        </div>
    );
};

export default UserRequests;